import { Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs/Rx';
import 'rxjs/add/operator/map';
import { ReplaySubject, Subject } from 'rxjs/';

import { CommunicationService } from './communication.service';
import { Questionnaire } from '../models/questionnaire';
import {
  QuestionnaireElement, QuestionnaireElementHeadline, QuestionnaireElementYesNo,
  QuestionnaireElementSlider, QuestionnaireElementSingleChoice, QuestionnaireElementTextDate,
  QuestionnaireElementMultipleChoice, QuestionnaireElementTextString,
  QuestionnaireElementTextArea
} from '../models/questionnaire-element';
import { Answersheet } from '../models/answersheet';
import { BasicError, Error403 } from '../models/error/error';

@Injectable()
export class HcpQuestionnaireService {
  private questionnairesOfStudyURL = '/api/v1/studies/##id##/questionnaires';
  private questionnairesDetailsURL = '/api/v1/questionnaires/';
  private questionnaireElementsURL = '/api/v1/questionnaires/##id##/elements';
  private answerSheetsURL = '/api/v1/studies/##sid##/members/##mid##/answersheets';
  private answerSheetDetailURL = '/api/v1/studies/##sid##/members/##mid##/answersheets/';
  private answerSheetsAlternativeUrl = '/api/v1/studies/##sid##/questionnaires/##qid##/answersheets';

  constructor(private communication: CommunicationService) { }

  public getQuestionnairesOfStudy(studyId: String, currentPage: number = 1): Subject<any> {
    const retReplay = new Subject();

    this.communication.get(this.questionnairesOfStudyURL.replace('##id##', studyId.toString()) + '?page=' +
      currentPage + '&limit=0').subscribe(json => {
        if (json instanceof BasicError) {
          console.log('----------------HCPQuestionnaireService----------: Error occured while reading questionnaires of study');

          if (json instanceof Error403) {
            console.log('----------------HCPQuestionnaireService----------: This user does' +
              ' not have the rights to read the questionnaires of this study');
          }

          retReplay.next(json);
          return;
        }

        // No Error occured, proceed with reading questionnaires
        const total_pages = json.meta.pagination.total_pages;
        const questionnaires = [];

        for (let i = 0; i < json.data.length; i++) {
          const questionnaireJSON = json.data[i];

          const quest = new Questionnaire(questionnaireJSON.id);
          if (questionnaireJSON.attributes.name !== undefined) { quest.setName(questionnaireJSON.attributes.name); };
          if (questionnaireJSON.attributes.title !== undefined) { quest.setTitle(questionnaireJSON.attributes.title); }
          if (questionnaireJSON.attributes.description !== undefined) { quest.setDescription(questionnaireJSON.attributes.description); }
          if (questionnaireJSON.attributes.origin !== undefined) { quest.setOrigin(questionnaireJSON.attributes.origin); }
          (questionnaireJSON.attributes.is_active !== undefined && questionnaireJSON.attributes.is_active === 1) ?
            quest.setActive(true) : quest.setActive(false);

          questionnaires.push(quest);
        }

        if (total_pages === 1) {
          this.getQuestionnairesDetails(questionnaires).subscribe(jj => {
            if (jj instanceof BasicError) {
              console.log('----------------HCPQuestionnaireService----------: Failed to get '
                + 'Questionnaire details. Return this data instead.');
              retReplay.next(questionnaires);
              return;
            }
            retReplay.next(jj);
          });
        } else if (currentPage >= total_pages) {
          retReplay.next(questionnaires);
        } else if (currentPage === 1) {
          this.getQuestionnairesOfStudy(studyId, (currentPage + 1)).subscribe(js => {
            if (js instanceof BasicError) {
              console.log('---------HCPQuestionnaireService-----: Failed to load following Questionnaires.');
              retReplay.next(questionnaires);
              return;
            }

            const q2 = questionnaires.concat(js);

            this.getQuestionnairesDetails(q2).subscribe(jj => {
              if (jj instanceof BasicError) {
                console.log('----------------HCPQuestionnaireService----------: Failed to get '
                  + 'Questionnaire details. Return this data instead.');
                retReplay.next(questionnaires);
                return;
              }
              retReplay.next(jj);
            });
          });
        } else {
          this.getQuestionnairesOfStudy(studyId, (currentPage + 1)).subscribe(js => {
            if (js instanceof BasicError) {
              console.log('---------HCPQuestionnaireService-----: Failed to load following Questionnaires.');
              retReplay.next(questionnaires);
              return;
            }

            const q2 = questionnaires.concat(js);

            retReplay.next(q2);
          });
        }
      });

    return retReplay;
  }

  /**
   * Getting detailed informations of questionnaires
   * @param questionnaires all questionnaire objects
   */
  public getQuestionnairesDetails(questionnaires): Subject<any> {
    const retReplay = new Subject();

    let count = 0;
    const retQuest = [];
    for (let i = 0; i < questionnaires.length; i++) {
      const quest = questionnaires[i];

      this.getQuestionnaireDetail(quest).subscribe(json => {
        count = count + 1;
        if (json instanceof BasicError) {
          console.log('---------HCPQuestionnaireService-----: Failed to load Questionnaire detail');
          retQuest.push(quest);
        } else {
          retQuest.push(json);
        }

        if (count >= questionnaires.length) {
          retReplay.next(retQuest);
        }
      });
    }

    return retReplay;
  }

  /**
   * Getting detail of a specific questionnaire
   * @param quest Questionnaire for request
   */
  public getQuestionnaireDetail(quest: Questionnaire): Subject<any> {
    const retReplay = new Subject();

    this.communication.get(this.questionnairesDetailsURL + quest.getId()).subscribe(json => {
      if (json instanceof BasicError) {
        retReplay.next(json);
        return;
      }

      if (json.data.attributes.name !== undefined) { quest.setName(json.data.attributes.name); };
      if (json.data.attributes.title !== undefined) { quest.setTitle(json.data.attributes.title); }
      if (json.data.attributes.description !== undefined) { quest.setDescription(json.data.attributes.description); }
      if (json.data.attributes.origin !== undefined) { quest.setOrigin(json.data.attributes.origin); }
      (json.data.attributes.is_active !== undefined && json.data.attributes.is_active === 1) ?
        quest.setActive(true) : quest.setActive(false);
      if (json.data.attributes.can_use !== undefined) { quest.setCanUse(json.data.attributes.can_use) };

      (json.data.attributes.is_onetime !== undefined && json.data.attributes.is_onetime === 1) ?
        quest.setOneTime(true) : quest.setOneTime(false);

      (json.data.attributes.is_multiple !== undefined && json.data.attributes.is_multiple === 1) ?
        quest.setMultiple(true) : quest.setMultiple(false);

      (json.data.attributes.is_schedule_changeable !== undefined && json.data.attributes.is_schedule_changeable === 1) ?
        quest.setScheduleChangeable(true) : quest.setScheduleChangeable(false);

      if (json.data.attributes.schedule !== undefined) { quest.setSchedule(json.data.attributes.schedule); }

      if (json.data.attributes.introtext !== undefined) { quest.setIntrotext(json.data.attributes.introtext); };

      if (json.data.attributes.outrotext !== undefined) { quest.setOutrotext(json.data.attributes.outrotext); }

      retReplay.next(quest);
    });

    return retReplay;
  }

  /**
   * This function returns the elements of a questionnaire
   * @param quest Questionnaire to get element of
   */
  public getQuestionnaireElements(quest: Questionnaire): Subject<any> {
    const retReplay = new Subject();

    this.communication.get(this.questionnaireElementsURL.replace('##id##', quest.getId().toString())).subscribe(json => {
      if (json instanceof BasicError) {
        console.log('---------HCPQuestionnaireService-----: Failed to load elements of study');
        retReplay.next(json);
        return;
      }

      const elements = [];

      for (let i = 0; i < json.data.attributes.elements.length; i++) {
        const element = json.data.attributes.elements[i];

        if (element.elementtype === 'elements/headlines') {
          // Is an Headline element
          const headerElem = new QuestionnaireElementHeadline();
          headerElem.setName(element.name);
          headerElem.setElementType(element.elementtype);
          headerElem.setPosition(element.position);

          for (let j = 0; j < element.translations.length; j++) {
            headerElem.setHeadline(element.translations[j].headline, element.translations[j].locale);
          }
          elements.push(headerElem);
          (element.is_active) ? headerElem.setActive(true) : headerElem.setActive(false);

        } else if (element.elementtype === 'elements/questions') {
          // Is an Question element
          let elem;
          switch (element.questiontype) {
            case 'YesNoSwitch':
              elem = new QuestionnaireElementYesNo();

              elem.setName(element.name);
              elem.setLabel(element.label);
              elem.setElementType(element.elementtype);
              (element.required) ? elem.setRequired(true) : elem.setRequired(false);
              (element.values !== undefined && elem.values !== null) ? elem.setValues(element.values) : elem.setValues([]);
              elem.setPosition(element.position);
              (element.is_active) ? elem.setActive(true) : elem.setActive(false);

              for (let j = 0; j < element.translations.length; j++) {
                const translations = element.translations[j];

                if (translations.question !== undefined && translations.question !== null) {
                  elem.setQuestion(translations.question, translations.locale);
                } else {
                  elem.setQuestion('', translations.locale);
                }

                if (translations.answers !== undefined && translations.answers !== null) {
                  elem.setAnswers(translations.answers, translations.locale);
                } else {
                  elem.setAnswers(elem.getValues(), translations.locale);
                }
              }

              elements.push(elem);
              break;
            case 'Slider':
            case 'SAMScaleFace':
            case 'SAMScaleBody':
              elem = new QuestionnaireElementSlider();

              elem.setName(element.name);
              elem.setLabel(element.label);
              elem.setElementType(element.elementtype);
              (element.required) ? elem.setRequired(true) : elem.setRequired(false);
              (element.values !== undefined && elem.values !== null) ? elem.setValues(element.values) : elem.setValues([]);
              elem.setPosition(element.position);
              (element.is_active) ? elem.setActive(true) : elem.setActive(false);

              for (let j = 0; j < element.translations.length; j++) {
                const translations = element.translations[j];

                if (translations.question !== undefined && translations.question !== null) {
                  elem.setQuestion(translations.question, translations.locale);
                } else {
                  elem.setQuestion('', translations.locale);
                }

                if (translations.answers !== undefined && translations.answers !== null) {
                  elem.setAnswers(translations.answers, translations.locale);
                } else {
                  elem.setAnswers(elem.getValues(), translations.locale);
                }
              }

              elements.push(elem);
              break;
            case 'SingleChoice':
              elem = new QuestionnaireElementSingleChoice();

              elem.setName(element.name);
              elem.setLabel(element.label);
              elem.setElementType(element.elementtype);
              (element.required) ? elem.setRequired(true) : elem.setRequired(false);
              (element.values !== undefined && elem.values !== null) ? elem.setValues(element.values) : elem.setValues([]);
              elem.setPosition(element.position);
              (element.is_active) ? elem.setActive(true) : elem.setActive(false);

              for (let j = 0; j < element.translations.length; j++) {
                const translations = element.translations[j];

                if (translations.question !== undefined && translations.question !== null) {
                  elem.setQuestion(translations.question, translations.locale);
                } else {
                  elem.setQuestion('', translations.locale);
                }

                if (translations.answers !== undefined && translations.answers !== null) {
                  elem.setAnswers(translations.answers, translations.locale);
                } else {
                  elem.setAnswers(elem.getValues(), translations.locale);
                }
              }

              elements.push(elem);
              break;
            case 'TextDate':
              elem = new QuestionnaireElementTextDate();

              elem.setName(element.name);
              elem.setLabel(element.label);
              elem.setElementType(element.elementtype);
              (element.required) ? elem.setRequired(true) : elem.setRequired(false);
              (element.values !== undefined && elem.values !== null) ? elem.setValues(element.values) : elem.setValues([]);
              elem.setPosition(element.position);
              (element.is_active) ? elem.setActive(true) : elem.setActive(false);

              for (let j = 0; j < element.translations.length; j++) {
                const translations = element.translations[j];

                if (translations.question !== undefined && translations.question !== null) {
                  elem.setQuestion(translations.question, translations.locale);
                } else {
                  elem.setQuestion('', translations.locale);
                }

                if (translations.answers !== undefined && translations.answers !== null) {
                  elem.setAnswers(translations.answers, translations.locale);
                } else {
                  elem.setAnswers(elem.getValues(), translations.locale);
                }
              }

              elements.push(elem);
              break;
            case 'MultipleChoice':
              elem = new QuestionnaireElementMultipleChoice();

              elem.setName(element.name);
              elem.setLabel(element.label);
              elem.setElementType(element.elementtype);
              (element.required) ? elem.setRequired(true) : elem.setRequired(false);
              (element.values !== undefined && elem.values !== null) ? elem.setValues(element.values) : elem.setValues([]);
              elem.setPosition(element.position);
              (element.is_active) ? elem.setActive(true) : elem.setActive(false);

              for (let j = 0; j < element.translations.length; j++) {
                const translations = element.translations[j];

                if (translations.question !== undefined && translations.question !== null) {
                  elem.setQuestion(translations.question, translations.locale);
                } else {
                  elem.setQuestion('', translations.locale);
                }

                if (translations.answers !== undefined && translations.answers !== null) {
                  elem.setAnswers(translations.answers, translations.locale);
                } else {
                  elem.setAnswers(elem.getValues(), translations.locale);
                }
              }

              elements.push(elem);
              break;
            case 'TextString':
              elem = new QuestionnaireElementTextString();

              elem.setName(element.name);
              elem.setLabel(element.label);
              elem.setElementType(element.elementtype);
              (element.required) ? elem.setRequired(true) : elem.setRequired(false);
              (element.values !== undefined && elem.values !== null) ? elem.setValues(element.values) : elem.setValues([]);
              elem.setPosition(element.position);
              (element.is_active) ? elem.setActive(true) : elem.setActive(false);

              for (let j = 0; j < element.translations.length; j++) {
                const translations = element.translations[j];

                if (translations.question !== undefined && translations.question !== null) {
                  elem.setQuestion(translations.question, translations.locale);
                } else {
                  elem.setQuestion('', translations.locale);
                }

                if (translations.answers !== undefined && translations.answers !== null) {
                  elem.setAnswers(translations.answers, translations.locale);
                } else {
                  elem.setAnswers(elem.getValues(), translations.locale);
                }
              }

              elements.push(elem);
              break;
            case 'TextArea':
              elem = new QuestionnaireElementTextArea();

              elem.setName(element.name);
              elem.setLabel(element.label);
              elem.setElementType(element.elementtype);
              (element.required) ? elem.setRequired(true) : elem.setRequired(false);
              (element.values !== undefined && elem.values !== null) ? elem.setValues(element.values) : elem.setValues([]);
              elem.setPosition(element.position);
              (element.is_active) ? elem.setActive(true) : elem.setActive(false);

              for (let j = 0; j < element.translations.length; j++) {
                const translations = element.translations[j];

                if (translations.question !== undefined && translations.question !== null) {
                  elem.setQuestion(translations.question, translations.locale);
                } else {
                  elem.setQuestion('', translations.locale);
                }

                if (translations.answers !== undefined && translations.answers !== null) {
                  elem.setAnswers(translations.answers, translations.locale);
                } else {
                  elem.setAnswers(elem.getValues(), translations.locale);
                }
              }

              elements.push(elem);
              break;
            default:
              console.log('---------HCPQuestionnaireService-----: Element not recognized');
              console.log(element.questiontype);
          }
        } else {
          // Not recognized
          console.log('---------HCPQuestionnaireService-----: Element not recognized');
          console.log(element.elementtype);
        }
      }

      retReplay.next(elements);
    });

    return retReplay;
  }

  /**
   * This Function loads all answersheets of a specified user.
   * With a given Questionnaire the answersheets will be filtered.
   * @param studyId studyid of hcp
   * @param memberId member id of patient in hcp group
   * @param quest questionnaire (filtered answers) [opional]
   * @param currentPage currentPage to be loaded [optional]
   */
  public getAnswersheetsOfMember(studyId: String, memberId: String, quest: Questionnaire = null, currentPage: number = 1): Subject<any> {
    const retReplay = new Subject();

    let url = this.answerSheetsURL.replace('##sid##', studyId.toString()).replace('##mid##', memberId.toString());

    if (quest !== null) {
      url = url + '?questionnaire_id=' + quest.getId().toString();
      url = url + '&page=' + currentPage + '&limit=0';
    } else {
      url = url + '?page=' + currentPage + '&limit=0';
    }


    // Request answersheets of this user
    this.communication.get(url).subscribe(json => {
      if (json instanceof BasicError) {
        console.log('---------HCPQuestionnaireService-----: Error in loading Answersheets of User');
        retReplay.next(json);
        return;
      }

      // Getting Answersheets
      const total: number = json.meta.pagination.total_pages;
      const ret = [];

      for (let i = 0; i < json.data.length; i++) {
        const answerJSON = json.data[i];
        const answersheet = new Answersheet(answerJSON.id);

        if (answerJSON.attributes.questionnaire_id !== undefined) {
          answersheet.setQuestionnaireId(answerJSON.attributes.questionnaire_id);
        };

        if (answerJSON.attributes.collected_at !== undefined) { answersheet.setCollectedAt(answerJSON.attributes.collected_at); }

        if (answerJSON.attributes.flags !== undefined) { answersheet.setFlags(answerJSON.attributes.flags); }

        ret.push(answersheet);
      }

      // If more pages are available load them first before proceeding
      if (total === 1) {
        // only one page available
        this.getAnswersheetsDetails(studyId, memberId, ret).subscribe(details => {
          if (details instanceof BasicError) {
            console.log('---------HCPQuestionnaireService-----: Error in loading Answersheetsdetails of User');
            retReplay.next(details);
            return;
          }

          retReplay.next(details);
        });
      } else if (currentPage >= total) {
        // Last Page
        retReplay.next(ret);
      } else if (currentPage === 1) {
        // More pages available but first page proceeds
        this.getAnswersheetsOfMember(studyId, memberId, quest, (currentPage + 1)).subscribe(ans => {
          if (ans instanceof BasicError) {
            console.log('---------HCPQuestionnaireService-----: Error in loading Answersheets of next page');
            retReplay.next(ret);
            return;
          }

          const ret2 = ret.concat(ans);
          this.getAnswersheetsDetails(studyId, memberId, ret2).subscribe(details => {
            if (details instanceof BasicError) {
              console.log('---------HCPQuestionnaireService-----: Error in loading Answersheetsdetails of User');
              retReplay.next(details);
              return;
            }

            retReplay.next(details);
          });
        })
      } else {
        // Pages between first and last
        this.getAnswersheetsOfMember(studyId, memberId, quest, (currentPage + 1)).subscribe(ans => {
          if (ans instanceof BasicError) {
            console.log('---------HCPQuestionnaireService-----: Error in loading Answersheets of next page');
            retReplay.next(ret);
            return;
          }

          const ret2 = ret.concat(ans);
          retReplay.next(ret2);
        })
      }
    });

    return retReplay;
  }


  /**
   * This function gets answersheets filtered, details of answersheets can be added as
   * filter
   * @param studyId studyid of study (necessary)
   * @param memberId memberid of member (necessary)
   * @param quest questionnaire of answersheet (necessary)
   * @param details details loading (necessary)
   * @param limit limit (amount) [optional]
   * @param sort sort elements with this [optional], example: 'age,-id' or '-id' or 'id'
   * @param filters filtering elements [optional], filtering: collected_at<1234567&collected_at>1123456
   */
  public getAnswersheetsOfMemberFiltered(studyId: String, memberId: String,
    quest: Questionnaire, details: Boolean = false, limit: Number = 0, sort: String = null, filters: String = null): Subject<any> {
    const retReplay = new Subject();

    let url = this.answerSheetsURL.replace('##sid##', studyId.toString()).replace('##mid##', memberId.toString());

    let firstAttr = false;

    if (quest !== null) {
      firstAttr = true;
      url = url + '?questionnaire_id=' + quest.getId().toString();
    }

    // Add limit
    if (firstAttr) {
      url = url + '&limit=' + limit;
    } else {
      url = url + '?limit=' + limit;
    }

    // Add sort if exist
    if (sort !== null) {
      url = url + '&sort=' + sort.toString();
    }

    // Add filter if exist
    if (filters !== null) {
      url = url + '&' + filters;
    }


    // Request answersheets of this user
    this.communication.get(url).subscribe(json => {
      if (json instanceof BasicError) {
        console.log('---------HCPQuestionnaireService-----: Error in loading Answersheets of User');
        retReplay.next(json);
        return;
      }

      // Getting Answersheets
      const ret = [];

      for (let i = 0; i < json.data.length; i++) {
        const answerJSON = json.data[i];
        const answersheet = new Answersheet(answerJSON.id);

        if (answerJSON.attributes.questionnaire_id !== undefined) {
          answersheet.setQuestionnaireId(answerJSON.attributes.questionnaire_id);
        };

        if (answerJSON.attributes.collected_at !== undefined) { answersheet.setCollectedAt(answerJSON.attributes.collected_at); }

        if (answerJSON.attributes.flags !== undefined) { answersheet.setFlags(answerJSON.attributes.flags); }

        ret.push(answersheet);
      }

      // Answersheets were loaded (limit is always necessary so no pages needed)
      // Decide if details must be loaded
      if (details) {
        // Load details before returning
        this.getAnswersheetsDetails(studyId, memberId, ret).subscribe(dd => {
          if (dd instanceof BasicError) {
            console.log('----HcpQuestionnaireService---: Details could not be loaded');
            retReplay.next(ret);
            return;
          }

          retReplay.next(dd);
        });
      } else {
        // Return data
        retReplay.next(ret);
      }
    });

    return retReplay;
  }

  /**
   *
   * @param studyId studyid of study
   * @param questId questionnaire id of questionnaire
   * @param memberId memberid [optional]
   * @param limit limit [limit = 0 all answersheets]
   * @param sort sorting for a specified attribute
   * @param filters filtering (like collected_at etc.)
   */
  public getAnswersheetsOfMemberAlternative(studyId: String, questId: String, memberId: String = null,
    limit: Number = 0, amount: Number = null,
    sort: String = null, filters: String = null): Subject<any> {
    const retReplay = new Subject();

    let url = this.answerSheetsAlternativeUrl.replace('##sid##', studyId.toString()).replace('##qid##', questId.toString());

    let firstAttr = false;

    if (memberId !== null) {
      firstAttr = true;
      url = url + '?user_id=' + memberId.toString();
    }

    // Add limit
    if (firstAttr) {
      url = url + '&limit=' + limit;
    } else {
      url = url + '?limit=' + limit;
    }

    // Add amount if there is any specified
    if (amount !== null) {
      url = url + '&amount=' + amount.valueOf();
    }

    // Add sort if exist
    if (sort !== null) {
      url = url + '&sort=' + sort.toString();
    }

    // Add filter if exist
    if (filters !== null) {
      url = url + '&' + filters;
    }


    // Request answersheets of this user
    this.communication.get(url).subscribe(json => {
      if (json instanceof BasicError) {
        console.log('---------HCPQuestionnaireService-----: Error in loading Answersheets of User');
        retReplay.next(json);
        return;
      }

      // Getting Answersheets
      const ret = [];

      for (let i = 0; i < json.data.length; i++) {
        const answerJSON = json.data[i];
        const answersheet = new Answersheet(answerJSON.id);

        if (answerJSON.attributes.questionnaire_id !== undefined) {
          answersheet.setQuestionnaireId(answerJSON.attributes.questionnaire_id);
        };

        if (answerJSON.attributes.collected_at !== undefined) { answersheet.setCollectedAt(answerJSON.attributes.collected_at); }

        if (answerJSON.attributes.flags !== undefined) { answersheet.setFlags(answerJSON.attributes.flags); }

        if (answerJSON.attributes.locale !== undefined) {
          answersheet.setLocale(answerJSON.attributes.locale);
        }

        if (answerJSON.attributes.sensordata !== undefined) {
          answersheet.setSensorData(answerJSON.attributes.sensordata);
        }

        if (answerJSON.attributes.client !== undefined) {
          answersheet.setClient(answerJSON.attributes.client);
        }

        if (answerJSON.attributes.user_id !== undefined) {
          answersheet.setUserId(answerJSON.attributes.user_id);
        }

        if (answerJSON.attributes.answers !== undefined) {
          answersheet.setAnswers(answerJSON.attributes.answers);
        } else {
          answersheet.setAnswers([]);
        }

        ret.push(answersheet);
      }

      retReplay.next(ret);
    });

    return retReplay;
  }

  /**
   * This function returns all answersheets with the necessary details (loaded from detail function)
   * @param studyId Study id of hcp group
   * @param memberId memberid of patient
   * @param answersheets answersheets to get details from
   */
  public getAnswersheetsDetails(studyId: String, memberId: String, answersheets: Array<Answersheet>): Subject<any> {
    const retReplay = new Subject();

    const answerDetails = [];
    let counter = 0;
    for (let i = 0; i < answersheets.length; i++) {
      this.getAnswersheetDetail(studyId, memberId, answersheets[i]).subscribe(ans => {
        if (ans instanceof BasicError) {
          console.log('---------HCPQuestionnaireService-----: Could not get details of answersheet with id: ' + answersheets[i].getId());
        } else {
          answerDetails.push(ans);
        }

        counter = counter + 1;
        if (counter >= answersheets.length) {
          // All Details finished loading
          retReplay.next(answerDetails);
        }
      });
    }

    return retReplay;
  }


  /**
   * This function fetchs the details of a specified answersheet
   * @param studyid studyid of hcp group
   * @param memberId memberid of patient
   * @param answersheet answersheet to get details from
   */
  public getAnswersheetDetail(studyid: String, memberId: String, answersheet: Answersheet): Subject<any> {
    const retResponse = new Subject();

    let url = this.answerSheetDetailURL.replace('##sid##', studyid.toString()).replace('##mid##', memberId.toString());
    url = url + answersheet.getId().toString();

    this.communication.get(url).subscribe(json => {
      if (answersheet instanceof BasicError) {
        console.log('---------HCPQuestionnaireService-----: Could not get details of answersheet with id: ' + answersheet.getId());
        retResponse.next(answersheet);
        return;
      }

      if (json.data.attributes.collected_at !== undefined) { answersheet.setCollectedAt(json.data.attributes.collected_at); }

      if (json.data.attributes.locale !== undefined) {
        answersheet.setLocale(json.data.attributes.locale);
      }

      if (json.data.attributes.sensordata !== undefined) {
        answersheet.setSensorData(json.data.attributes.sensordata);
      }

      if (json.data.attributes.client !== undefined) {
        answersheet.setClient(json.data.attributes.client);
      }

      if (json.data.attributes.answers !== undefined) {
        answersheet.setAnswers(json.data.attributes.answers);
      } else {
        answersheet.setAnswers([]);
      }

      retResponse.next(answersheet);
    });

    return retResponse;
  }

}



// WEBPACK FOOTER //
// C:/Users/andyw/Documents/Uni/Master/Masterprojekt/Angular/src/app/services/hcp-questionnaire.service.ts